import { ToxnotSignInRequiredComponent } from "../BaseApplicationComponents/ToxnotSignInRequiredComponent";
import {
    DataCollectionAiAgentOperationType,
    LinkedArticleType,
    QuestionnairePageType,
    QuestionnaireProcessingType,
    QuestionnaireQuestion,
    QuestionRegulatoryData,
    QuestionRevision, ScipSubmissionData
} from "../../../store/Models/Surveys/SurveyDataModel";
import { FileTableEntry } from "../../../store/Models/Documents/DocumentModels";
import { PlausibilityData, isSurveyReadonly } from "../../../store/StateControllers/Survey/SurveyResponse/SurveyResponseModels";
import { ToxnotComponentProperties } from "../BaseApplicationComponents/ToxnotComponent";
import * as SurveyResponseController from "../../../store/StateControllers/Survey/SurveyResponse/SurveyResponseController";
import { SurveyResponseState } from "../../../store/StateControllers/Survey/SurveyResponse/SurveyResponseStates";
import { AISurveyAgentState, getActiveAIOperationFromState } from "../../../store/StateControllers/Survey/SurveyResponse/AISurveyAgentController";
import { connect } from "react-redux";
import { ApplicationState } from "../../../store";
import { ToxnotServiceClient } from "../../../store/ToxnotServiceClient";
import { TeamRole } from "../../../store/Models/UserContext";
import Question from "../Question";
import SaveDataManager from "../../../utilities/SaveDataManager";
import { ScipCustomLogicManager } from "../../../store/Managers/CustomLogicManagers/ScipCustomLogicManager";
import { LoadingSpinner, LoadingSpinnerSize } from "../LoadingSpinner";
import ConcernElements from "../ConcernElements/ConcernElements";
import LinkedArticles from "../LinkedArticles/LinkedArticles";
import { ScipDataManager } from "../../../store/Managers/SurveyResponse/ScipDataManager";
import TextInput from "../TextInput";
import CheckBox from "../CheckBox";

import './styles.css';

export interface ScipSurveyPageLocalProps {
    questionsList: QuestionnaireQuestion[];
    questionRegulatoryData: QuestionRegulatoryData[];
    onResponseChange?: (
        value: string,
        valueComment: string,
        questionIndex: number,
        files?: FileTableEntry[],
        attchedFileIds?: string[]
    ) => void;
    pageTitle: string;
    regulationId: string;
    fileList: FileTableEntry[];
    inventoryId?: string;
    pageRevisionInstructions?: string;
    questionsRevisionInstructions?: QuestionRevision[] | null;
    questionnairePageType?: QuestionnairePageType;
    onComponentDidMount: () => Promise<void>;
    plausibilityData: PlausibilityData[] | undefined;
}

type ScipSurveyPageProps =
    ToxnotComponentProperties &
    typeof SurveyResponseController.actionCreators &
    SurveyResponseState &
    { aiSurveyAgentState?: AISurveyAgentState } &
    ScipSurveyPageLocalProps;

interface LocalState {
    // stack of submission data tree currently being edited.
    // the last element is the active model under edit
    // the first element is the root data for the inventory we are in
    // each element in between are the elements going down the tree to the active element
    submissionDataStack: ScipSubmissionData[];
    isDataLoaded: boolean;
    hasSCIPNumber: boolean;
    currentSCIPNumber: string;
}

class ScipSurveyPage extends ToxnotSignInRequiredComponent<ScipSurveyPageProps, LocalState> {
    private _serviceClient: ToxnotServiceClient;
    private _saveDataManager: SaveDataManager<ScipSubmissionData>;
    private _scipCustomLogicManager: ScipCustomLogicManager;

    public constructor(props: ScipSurveyPageProps) {
        super(props);

        this._scipCustomLogicManager = this.props.regulationManager as ScipCustomLogicManager;
        this._serviceClient = new ToxnotServiceClient();
        this._saveDataManager = new SaveDataManager<ScipSubmissionData>(this.saveScipDataHandler, this.props.appServices);
        this._saveDataManager.enable();

        this.state = {
            submissionDataStack: [],
            isDataLoaded: false,
            hasSCIPNumber: false,
            currentSCIPNumber: ''
        };
    }

    public componentWillUnmount() {
        const inventoryQuestionnaireList = this.props.selectedInventory?.questionnaireList ?? [];

        this.props.resetSelectedScipSubmissionsData(this.state.submissionDataStack[0], inventoryQuestionnaireList, this.props.navContext.surveyQuestionnaireIndex, this.props.navContext.surveyQuestionnairePageIndex);
    }

    public async componentDidMount() {
        if (!this.props.selectedInventory) {
            return;
        }

        const apiResult = await this._serviceClient.scipSubmissionGet(this.props.selectedInventory.inventoryId, false, true);

        if (apiResult.success && apiResult.data) {
            this.setState({
                submissionDataStack: [apiResult.data.scipSubmissionData]
            });

            this.props.updateCurrentlySelectedScipSubmissionsData(apiResult.data.scipSubmissionData);
        }

        this.setState({
            isDataLoaded: true
        });
    }

    private saveScipDataHandler = async (data: ScipSubmissionData) => {
        const apiResult = await this._serviceClient.scipSubmissionUpdate(data, false);

        return apiResult.success;
    }

    private onAiHelpfulStateChange = async (questionIndex: number, isHelpful: -1 | 0 | 1): Promise<void> => {
        this.props.updateQuestionAiHelpfulState(isHelpful, questionIndex);
    }

    protected getDisallowedRoles(): TeamRole[] {
        return [TeamRole.Marketing, TeamRole.Viewer];
    }

    private getActiveScipSubmissionDataUnderEdit() {
        if (this.state.submissionDataStack.length === 0) {
            throw new Error("Invalid operation - must have at least one item on the stack");
        } else {
            return this.state.submissionDataStack[this.state.submissionDataStack.length - 1];
        }
    }

    private getQuestionsForComplexObject() {
        const activeScipSubmissionDataUnderEdit = this.getActiveScipSubmissionDataUnderEdit();

        const dataQuestionScipDataKeys = new Set<string>(
            [
                ScipDataManager.ScipDataKeyPrimaryArticleIdentifier,
                ScipDataManager.ScipDataKeyArticleCategory,
                ScipDataManager.ScipCharacteristics,
                ScipDataManager.ScipDataKeySafeUseInstructions
            ]
        );

        const questionsForComplexObject = [...this.props.questionsList.filter(q => (q.scipDataKey !== null) && dataQuestionScipDataKeys.has(q.scipDataKey))];

        ScipDataManager.fillScipQuestionsWithScipData(questionsForComplexObject, activeScipSubmissionDataUnderEdit);

        return questionsForComplexObject;
    }

    private renderComplexObjectEditor() {
        const questionsForComplexObject = this.getQuestionsForComplexObject();

        return (
            <div className="complex-objects-wrapper">
                <div className="m-b-10">
                    <button type={"button"} className={"btn btn-default"} onClick={e => this.changeToArticleAsSuchButton_click()}>{this.localizeString("Go back and change this Material to an Article")}</button>
                </div>
                <div className="hierarchy-breadcrumb-container">
                    <ol className="breadcrumb m-b-10">
                        {this.renderBreadCrumbLinks()}
                    </ol>
                </div>
                <div className="has-scip-number-checkbox-wrapper m-b-10">
                    <CheckBox
                        checkboxLabelText={this.localizeString("I have a SCIP number")}
                        checked={this.state.hasSCIPNumber}
                        onValueChange={value => { this.iHaveScipNumberCheckbox_onChange(value) }}
                        isDisabled={isSurveyReadonly(this.props.coreData, getActiveAIOperationFromState(this.props.aiSurveyAgentState, DataCollectionAiAgentOperationType.Survey))}>
                    </CheckBox>
                </div>
                {!this.state.hasSCIPNumber && questionsForComplexObject.map((q, index) => (
                    <div key={`${this.props.pageTitle}-${index}`} className="survey-question m-b-10">
                        <Question
                            key={this.getActiveScipSubmissionDataUnderEdit().scipSubmissionId}
                            hasIngredientDisclosure={this.props.selectedInventory?.hasBomPage}
                            bomHasAtLeastOneIngredient={true}
                            regulationLogicManager={this.props.regulationManager!}
                            pageTitle={this.props.pageTitle}
                            onResponseChange={this.scipComplexObjectQuestion_onResponseChanged}
                            onAiHelpfulStateChange={this.onAiHelpfulStateChange}
                            appServices={this.props.appServices}
                            questionIndex={index}
                            question={q}
                            displayIndex={index}
                            parentDisplayIndex={0}
                            localizer={this.props.appServices}
                            fileList={this.props.fileList}
                            inventoryId={this.props.inventoryId}
                            responseId={this.props.responseId}
                            regulationId={this.props.regulationId}
                            revisionInstructions={""}
                            language={this.props.insActiveLanguage}
                            requiredOverride={true}
                            isDisabled={isSurveyReadonly(this.props.coreData, getActiveAIOperationFromState(this.props.aiSurveyAgentState, DataCollectionAiAgentOperationType.Survey))} // submitted or AI processing
                            supplierRequiredToDiscloseRegulatoryIngredients={this.props.coreData?.request?.metadata.requireSuppliersToDiscloseRegulatoryIngredients ?? false}
                            navigateToBomPage={() => this.props.setSurveyBomPage(this.props.navContext.inventoryNavIndex)}
                            extensionRequested={this.props.coreData?.extensionRequest?.inventoryIds?.some(i => i === this.props.selectedInventory?.inventoryId)}
                            extensibleColumnNames={(this.props.selectedInventory?.extensibleColumnNames ?? [])}
                            plausibilityData={this.props.plausibilityData}
                            visibleOverride={true}
                            questionnaireProcessingType = {QuestionnaireProcessingType.Scip}
                        />
                    </div>
                ))}
                {this.state.hasSCIPNumber &&
                    <div className="m-b-20">
                        <label>
                            {this.localizeString("SCIP number")}
                        </label>
                        <span className="p-relative m-l-5">
                            <span className="text-danger p-absolute required-asterisk">*</span>
                        </span>
                        <TextInput value={this.state.currentSCIPNumber}
                            onValueChange={value => this.scipNumberQuestion_onResponseChanged(value, this.state.hasSCIPNumber)}></TextInput>
                    </div>
                }
            </div>
        );
    }

    private onSubArticleSelect(subArticle: ScipSubmissionData) {
        let submissionDataStack = [...this.state.submissionDataStack];

        submissionDataStack.push(subArticle);

        this.setState({
            submissionDataStack: submissionDataStack,
            hasSCIPNumber: subArticle.isScipNumberManuallyEntered ?? false,
            currentSCIPNumber: subArticle.scipNumber ?? ''
        });

        this.props.updateCurrentlySelectedScipSubmissionsData(subArticle);

        document.querySelector('.survey-response-body-wrapper')?.scrollTo(0, 0);
    }

    private renderBreadCrumbLinks(): JSX.Element[] {
        return (
            this.state.submissionDataStack.map((ssd, index) => {
                return (
                    <li key={index}>
                        <a role="button" className="hierarchy-breadcrumb-link" onClick={(e) => this.setActiveScipSubmissionDataUnderEdit(ssd)}>
                            {this.localizeString(ssd.articleName)}
                        </a>
                    </li>
                )
            })
        );
    }

    private setActiveScipSubmissionDataUnderEdit(scipSubmissionData: ScipSubmissionData) {
        let submissionDataStack = [...this.state.submissionDataStack];
        let currentScipSubmissionDataIndex = submissionDataStack.findIndex(value => value === scipSubmissionData);

        if (currentScipSubmissionDataIndex !== -1) {
            submissionDataStack = submissionDataStack.slice(0, currentScipSubmissionDataIndex + 1);

            this.props.updateCurrentlySelectedScipSubmissionsData(scipSubmissionData);
        }

        this.setState({
            submissionDataStack: submissionDataStack
        });
    };

    private isChildActiveSubmissionDisplayed() {
        const activeScipSubmissionDataUnderEdit = this.getActiveScipSubmissionDataUnderEdit();

        return activeScipSubmissionDataUnderEdit !== this.state.submissionDataStack[0];
    }

    public renderConcreteComponent() {
        if (!this._scipCustomLogicManager.shouldShowScip()) {
            return <></>;
        }

        if (!this.state.isDataLoaded) {
            return <LoadingSpinner size={LoadingSpinnerSize.Medium} />
        }

        const bomHasAtLeastOneIngredient = this.props.selectedInventory
            ?.inventoryRows
            .some(r => (r.assignedSubstanceId !== null) || (r.assignedPrivateSubstanceId !== null) || (r.overrideCasNumber !== null))
            ?? false;

        const activeScipSubmissionDataUnderEdit = this.getActiveScipSubmissionDataUnderEdit();

        return (
            <>
                {this.state.submissionDataStack.length === 1 && this.props.questionsList.map((q, index) => (
                    <div key={`${this.props.pageTitle}-${index}`} className="survey-question m-b-20">
                        <Question
                            hasIngredientDisclosure={this.props.selectedInventory?.hasBomPage}
                            bomHasAtLeastOneIngredient={bomHasAtLeastOneIngredient}
                            regulationLogicManager={this.props.regulationManager!}
                            pageTitle={this.localizeString(this.props.pageTitle)} // Localize page title
                            onResponseChange={this.scipMainObjectQuestion_onResponseChanged}
                            onAiHelpfulStateChange={this.onAiHelpfulStateChange}
                            appServices={this.props.appServices}
                            questionIndex={index}
                            question={q}
                            displayIndex={this._scipCustomLogicManager.getDisplayIndex(index)}
                            parentDisplayIndex={0}
                            localizer={this.props.appServices}
                            fileList={this.props.fileList}
                            inventoryId={this.props.inventoryId}
                            responseId={this.props.responseId}
                            regulationId={this.props.regulationId}
                            revisionInstructions={this.props.questionsRevisionInstructions?.firstOrDefault(r => r.pageIndex == this.props.navContext.surveyQuestionnairePageIndex && r.questionIndex == index)?.revisionInstructions}
                            language={this.props.insActiveLanguage}
                            isDisabled={isSurveyReadonly(this.props.coreData, getActiveAIOperationFromState(this.props.aiSurveyAgentState, DataCollectionAiAgentOperationType.Survey))} // submitted or AI processing
                            supplierRequiredToDiscloseRegulatoryIngredients={this.props.coreData?.request?.metadata.requireSuppliersToDiscloseRegulatoryIngredients ?? false}
                            navigateToBomPage={() => this.props.setSurveyBomPage(this.props.navContext.inventoryNavIndex)}
                            extensionRequested={this.props.coreData?.extensionRequest?.inventoryIds?.some(i => i === this.props.selectedInventory?.inventoryId)}
                            extensibleColumnNames={(this.props.selectedInventory?.extensibleColumnNames ?? [])}
                            plausibilityData={this.props.plausibilityData}
                            visibleOverride={this._scipCustomLogicManager.questionIsVisible(q)}
                            questionnaireProcessingType = {QuestionnaireProcessingType.Scip}
                        />
                    </div>
                ))}
                {this.state.submissionDataStack.length > 1 && this.renderComplexObjectEditor()}
                {activeScipSubmissionDataUnderEdit.concernElements.length > 0 &&
                    <ConcernElements existingConcernElements={activeScipSubmissionDataUnderEdit.concernElements}
                        localizer={this.props.appServices}
                        cultureName={this.props.appServices.userContext!.cultureName}
                        registerPopoversCb={this.registerPopovers}>
                    </ConcernElements>
                }
                {(this._scipCustomLogicManager.isComplexObject() || this.isChildActiveSubmissionDisplayed()) && activeScipSubmissionDataUnderEdit.linkedArticles.length > 0 &&
                    <LinkedArticles existingLinkedArticles={activeScipSubmissionDataUnderEdit.linkedArticles}
                        localizer={this.props.appServices}
                        onSubArticleSelect={(subArticle) => this.onSubArticleSelect(subArticle)}
                        registerPopoversCb={this.registerPopovers}>
                    </LinkedArticles>
                }
            </>
        );
    }

    private changeToArticleAsSuchButton_click() {
        this.setActiveScipSubmissionDataUnderEdit(this.state.submissionDataStack[0]);

        // reset the answer to the question about whether this is a complex object or article
        this.props.onResponseChange?.(
            "Article, as such",
            "",
            4
        );
    }

    private scipMainObjectQuestion_onResponseChanged = async (
        value: string,
        valueComment: string,
        questionIndex: number
    ) => {
        const s = this.getActiveScipSubmissionDataUnderEdit();
        const newSubmissionStack = [...this.state.submissionDataStack];
        const question = this.props.questionsList[questionIndex];

        newSubmissionStack.pop();

        if (question && question.scipDataKey === ScipDataManager.HasScipNumber) {
            s.isScipNumberManuallyEntered = value.toLowerCase() === "yes";
        }

        // Handle EU production question - set SCIP flags based on selection
        if (question && question.scipDataKey === ScipDataManager.ScipDataKeyEuProduction) {
            const euProductionOptions = ["Both EU produced and imported", "EU imported", "EU produced"];
            const isEuProductionSelected = euProductionOptions.some(option =>
                option.toLowerCase() === value.toLowerCase()
            );

            if (isEuProductionSelected) {
                s.isScipNumberManuallyEntered = true;
                s.hasScipNumber = "Yes";
            }
        }

        newSubmissionStack.push(s);

        this.setState({
            submissionDataStack: newSubmissionStack
        });

        await this.props.onResponseChange?.(value, valueComment, questionIndex);

        await this._saveDataManager.saveAsync(newSubmissionStack[0]);
    }

    private iHaveScipNumberCheckbox_onChange = async (
        value: boolean
    ) => {
        await this.setState({ hasSCIPNumber: value });

        this.updateChildScipSubmissionData(this.state.currentSCIPNumber, false, undefined, value);
    }

    private scipNumberQuestion_onResponseChanged = async (
        value: string,
        isScipNumberManuallyEntered: boolean
    ) => {
        await this.setState({ currentSCIPNumber: value });

        this.updateChildScipSubmissionData(value, false, undefined, isScipNumberManuallyEntered);
    }

    private scipComplexObjectQuestion_onResponseChanged = (
        value: string,
        valueComment: string,
        questionIndex: number,
        files?: FileTableEntry[],
        attchedFileIds?: string[]
    ) => {
        this.updateChildScipSubmissionData(value, true, questionIndex);
    }

    private updateChildScipSubmissionData = async (
        value: string,
        isScipComplexObjectUpdate: boolean,
        questionIndex?: number,
        isScipNumberManuallyEntered?: boolean
    ) => {
        const s = this.getActiveScipSubmissionDataUnderEdit();

        const newSubmissionStack = [...this.state.submissionDataStack];

        newSubmissionStack.pop();

        if (isScipComplexObjectUpdate && questionIndex !== undefined) {
            const questionsForComplexObject = this.getQuestionsForComplexObject();
            const question = questionsForComplexObject[questionIndex];

            switch (question.scipDataKey) {
                case ScipDataManager.ScipDataKeyArticleCategory:
                    s.articleCategoryIds = JSON.parse(value);
                    break;
                case ScipDataManager.ScipCharacteristics:
                    s.characteristics = JSON.parse(value);
                    break;
                case ScipDataManager.ScipDataKeySafeUseInstructions:
                    const safeUseInstructions = ScipDataManager.safeUseInstructionParse(value);

                    s.safeUseInstructions = safeUseInstructions.safeUseInstructions;
                    s.noSafeUseInstructionRequired = safeUseInstructions.noSafeUseInstructionRequired;
                    break;
                case ScipDataManager.ScipDataKeyPrimaryArticleIdentifier:
                    s.primaryArticleIdentifier = ScipDataManager.parseArticleIdentifierValue(value);
                    break;
            }
        } else {
            s.scipNumber = value;
            s.isScipNumberManuallyEntered = isScipNumberManuallyEntered;
        }

        newSubmissionStack.push(s);

        this.setState({
            submissionDataStack: newSubmissionStack
        });

        await this._saveDataManager.saveAsync(newSubmissionStack[0]);

        if (this.props.selectedInventory?.inventoryId) {
            await this.props.updateValidationResult([this.props.selectedInventory?.inventoryId]);
        }
    }

    private registerPopovers = (popoverIds: string[]) => {
        for (let i = 0; i < popoverIds.length; i++) {
            let popover = $(`#${popoverIds[i]}`);

            popover.popover({ // initialize popover
                trigger: "manual"
            });

            popover.off("click").on("click", () => popover.popover("toggle")); // toggle popover manually on click

            popover.on("shown.bs.popover", () => {
                $(document).off("click").on("click", documentClickCb); // handle popover hide when clicking the rest of the page
                $(".survey-response-body-wrapper").off("scroll").on("scroll", () => popover.popover("hide")); // handle popover hide when page is scrolled
            });

            const documentClickCb = (e: any) => {
                const target = $(e.target);
                const closestPopover = target.closest("[data-toggle=popover]");

                if (!target.is(popover) && !closestPopover.is(popover)) {
                    popover.popover("hide");
                }
            }
        }
    }
}

export default connect(
    (state: ApplicationState) => ({ ...state.surveyResponseState, aiSurveyAgentState: state.aiSurveyAgentState, appServices: state.appServices }),
    SurveyResponseController.actionCreators
)(ScipSurveyPage as any);
